import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import AppBreadcrumbs from 'components/AppBreadcrumbs';
import PageLayout from 'layouts/PageLayout';
import { useAsyncFn } from 'react-use';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import {
  getUserDocuments,
  setUserDocuments,
  downloadUserDocument,
  deleteUserDocument,
  getUser,
  updateUser,
} from 'http/users';
import {
  applyStudentRequiredTrainingSessions,
  createStudentExternalEvidence,
  deleteStudentEvidenceCertificate,
  deleteStudentExternalEvidence,
  deleteTrainingSessionCertificate,
  downloadStudentEvidenceCertificate,
  downloadStudentEvidencesExample,
  downloadTrainingSessionCertificate,
  getStudentEvidences,
  getStudentRequiredTrainingSessions,
  getStudentTrainingSessionsInProgress,
  getStudentTrainingSessionsPassed,
  getStudentTrainingSessionsScheduled,
  importStudentEvidences,
  removeTrainingSessionParticipant,
  updateStudentExternalEvidence,
  uploadStudentEvidenceCertificate,
  uploadTrainingSessionCertificate,
} from 'http/trainingSessions';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { actions as modalActions } from 'redux/modal';
import { actions as tableActions } from 'redux/table';
import { routesAliases } from 'router/routesAliases';
import { transformDataToSelectOptions, getConfirmationModalConfig } from 'utils';
import NotFound from 'pages/NotFound';
import { useTranslation } from 'react-i18next';
import StudentsForm from '../Form';
import TrainingSessionDetails from './TrainingSessionDetails/index';
import StudentRoleChangeModal from './StudentRoleChangeModal';

const useStyles = makeStyles(theme => ({
  courseDetailsContent: {
    padding: theme.spacing(2, 5),
  },
  pageContent: {
    padding: theme.spacing(4, 5),
  },
}));

const Edit = ({ routeProps }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const breadCrumbsItems = [
    {
      title: t(routeProps.parentTitle),
      link: routeProps.parentPath,
    },
    {
      title: t(routeProps.title),
      isActive: true,
    },
  ];

  const routerMatch = useRouteMatch();
  const { id: userId } = routerMatch.params;

  const actions = {
    getUserDocuments: getUserDocuments.bind(null, userId),
    setUserDocuments: setUserDocuments.bind(null, userId),
    downloadUserDocument,
    deleteUserDocument: deleteUserDocument.bind(null),
    getUser: getUser.bind(null, userId),
  };

  const { station } = useSelector(state => state.auth.profile);

  const [notFoundError, setNotFoundError] = useState(false);
  const [initialFormState, setInitialFormState] = useState(false);
  const [assignedJobTasks, setAssignedJobTasks] = useState([]);

  const [fetchUserState, fetchUserData] = useAsyncFn(async () => {
    const userData = await getUser(userId);
    if (!userData.error) {
      setAssignedJobTasks(userData.jobTasks || []);
      setInitialFormState({
        firstName: (userData.info && userData.info.firstName) || '',
        lastName: (userData.info && userData.info.lastName) || '',
        email: userData.email || '',
        company: (userData.info && userData.info.info && userData.info.info.company) || '',
        country: (userData.info && userData.info.info && userData.info.info.country) || '',
        phone: (userData.info && userData.info.info && userData.info.info.phone) || '',
        jobTasks: transformDataToSelectOptions(userData.jobTasks, 'id', 'name'),
        ids: (userData.info && userData.info.ids) || '',
        station: station || [],
        department: (userData.info && userData.info.info && userData.info.info.department) || '',
        stationsIds: userData.stations.map(item => item.id),
      });
    } else if (userData.statusCode === 404) {
      setNotFoundError(true);
      return false;
    }
    return true;
  });

  useEffect(() => {
    fetchUserData(userId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [state, saveStudentChanges] = useAsyncFn(async updatePayload => {
    const data = await updateUser(updatePayload, userId);
    if (data.error) {
      return data;
    }

    history.push(routesAliases.studentsList);
    return data;
  }, []);

  const editStudent = async formData => {
    return saveStudentChanges(formData);
  };

  const handleRoleChangeSuccess = async () => {
    await fetchUserData(userId);
    dispatch(tableActions.forceUpdateTable(true));
  };

  const openRoleChangeModal = () => {
    dispatch(
      modalActions.openModal(
        {
          size: 'large',
          title: t('students.roleChangeDialogTitle'),
        },
        <StudentRoleChangeModal
          userId={userId}
          initialJobTasks={initialFormState?.jobTasks || []}
          onSuccess={handleRoleChangeSuccess}
        />,
      ),
    );
  };

  const handleCloseModal = () => {
    dispatch(modalActions.closeModal());
    dispatch(tableActions.forceUpdateTable(true));
  };

  const [participantRemoving, removeParticipant] = useAsyncFn(async sessionId => {
    const data = await removeTrainingSessionParticipant(sessionId, userId);
    if (!data.error && !(data.statusCode && data.statusCode >= 400)) {
      handleCloseModal();
      dispatch(tableActions.setTableDeleting(true));
      dispatch(tableActions.forceUpdateTable(true));
    }
  });

  const handleRemoveParticipant = event => {
    const sessionId = event.currentTarget.dataset.key;
    const [config, element] = getConfirmationModalConfig({
      title: t('trainingSessionDetails.removeParticipantTitle'),
      confirmAction: () => removeParticipant(sessionId),
      rejectAction: handleCloseModal,
      message: t('trainingSessionDetails.removeParticipantMessage'),
    });
    dispatch(modalActions.openModal(config, element));
  };

  const editSession = event => {
    const sessionId = event.currentTarget.dataset.key;
    history.push(`${routesAliases.editTrainingSession}/${sessionId}`);
  };

  return notFoundError ? (
    <NotFound />
  ) : (
    <PageLayout
      pageTitle={<AppBreadcrumbs items={breadCrumbsItems} />}
      classes={{ content: classes.pageContent }}
      additionSections={[
        {
          contentClass: classes.courseDetailsContent,
          contentHeader: t('students.trainingSessionDetails'),
          component: (
            <TrainingSessionDetails
              userId={userId}
              isUserDataLoaded={!fetchUserState.loading && !!initialFormState}
              assignedJobTasks={assignedJobTasks}
              helpers={{
                getDocuments: actions.getUserDocuments,
                setDocuments: actions.setUserDocuments,
                downloadDocument: actions.downloadUserDocument,
                deleteDocument: actions.deleteUserDocument,
                getRequiredSessions: getStudentRequiredTrainingSessions.bind(null, userId),
                applyRequiredSessions: applyStudentRequiredTrainingSessions,
                getSessionsInProgress: getStudentTrainingSessionsInProgress.bind(null, userId),
                getSessionsScheduled: getStudentTrainingSessionsScheduled.bind(null, userId),
                getSessionsPassed: getStudentTrainingSessionsPassed.bind(null, userId),
                getEvidencesHistory: getStudentEvidences.bind(null, userId),
                createExternalEvidence: createStudentExternalEvidence.bind(null, userId),
                updateExternalEvidence: updateStudentExternalEvidence,
                deleteExternalEvidence: deleteStudentExternalEvidence,
                uploadEvidenceCertificate: uploadStudentEvidenceCertificate,
                downloadEvidenceCertificate: downloadStudentEvidenceCertificate,
                deleteEvidenceCertificate: deleteStudentEvidenceCertificate,
                importEvidences: importStudentEvidences.bind(null, userId),
                downloadEvidencesExample: downloadStudentEvidencesExample.bind(null, userId),
                removeParticipant: handleRemoveParticipant,
                editSession,
                setCertificate: uploadTrainingSessionCertificate,
                getEditorData: actions.getUser,
                downloadCertificate: downloadTrainingSessionCertificate,
                deleteCertificate: deleteTrainingSessionCertificate,
              }}
              routesAliases={routesAliases}
            />
          ),
        },
      ]}
      showLoader={fetchUserState.loading || participantRemoving.loading}
    >
      <StudentsForm
        isLoading={state.loading}
        onSubmit={editStudent}
        studentData={initialFormState}
        routesAliases={routesAliases}
        type="edit"
        currentRoleIds={(initialFormState?.jobTasks || []).map(item => item.value)}
        onOpenRoleChange={openRoleChangeModal}
      />
    </PageLayout>
  );
};

Edit.propTypes = {
  routeProps: PropTypes.shape({
    parentPath: PropTypes.string,
    parentTitle: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
};

export default Edit;
